/**
 * Licenses admin page.
 *
 * Layout:
 *   PageHeader → HeroPanel (total/active/expiring/revoked)
 *              → 6-month expiry timeline strip
 *              → Toolbar (status chips + plan filter + Issue button)
 *              → DataTable (StatusChip per row)
 *              → IssueDrawer + DetailDrawer (audit trail + billing actions)
 */
import { useEffect, useId, useMemo, useRef, useState, cloneElement, isValidElement, type JSX, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  KeyRound, RefreshCw, Plus, Trash2, Pause, Play, ShieldOff, ScrollText,
  CalendarClock, CreditCard, RotateCcw, Send,
} from 'lucide-react';
import {
  AdminApiClient,
  type LicenseRow, type LicenseDetail, type CustomerRow, type EngineRow, type AuditLogRow,
} from '../api';
import { useSession } from '../session';
import { usePermissions } from '../lib/permissions';
import { failureFrom, userMessageFor, type Failure } from '../lib/api-state';
import { AccessNotice } from '../components/ui/AccessNotice';
import { SecretValue } from '../components/ui/SecretValue';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, bandFromStatus } from '../components/ui/StatusChip';
import { DataTable, PrimaryWithSub, type Column } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';

type StatusFilter = 'all' | 'active' | 'past_due' | 'suspended' | 'revoked' | 'expired';

const PLAN_TONES: Record<string, string> = {
  starter: '#94a3b8',
  business: '#22d3ee',
  enterprise: '#c084fc',
  trial: '#fb923c',
};

/** Display order for the Plan column sort: smallest plan first. */
const PLAN_RANK: Record<string, number> = { trial: 0, starter: 1, business: 2, enterprise: 3 };

export function LicensesPage(): JSX.Element {
  const { state } = useSession();
  const [rows, setRows] = useState<LicenseRow[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [openDetailId, setOpenDetailId] = useState<string | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );
  const perms = usePermissions();
  const canMutate = perms.canWrite;
  const canDelete = perms.canOwner;

  // Deep link from the dashboard ("Issue license"): open the issue form
  // directly when this role may issue, otherwise just explain why not.
  useEffect(() => {
    const wantsIssue = searchParams.get('issue') === '1';
    const focusId = searchParams.get('focus');
    if (!wantsIssue && !focusId) return;
    if (wantsIssue && canMutate) setIssueOpen(true);
    // Deep links from Servers / search / marketplace ("?focus=<license id>").
    if (focusId) setOpenDetailId(focusId);
    const next = new URLSearchParams(searchParams);
    next.delete('issue');
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canMutate]);

  useEffect(() => {
    setRefreshing(true);
    api.licenses({ limit: 500 })
      .then((r) => { setRows(r.licenses); setErr(null); })
      .catch((e) => { setRows(null); setErr(failureFrom(e, 'the license list')); })
      .finally(() => setRefreshing(false));
  }, [api, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const counts = useMemo(() => {
    const now = Date.now();
    const in30 = now + 30 * 24 * 3600 * 1000;
    let total = 0, active = 0, expiring = 0, revoked = 0;
    for (const r of rows ?? []) {
      total += 1;
      if (r.status === 'active') active += 1;
      if (r.status === 'revoked') revoked += 1;
      if (r.expires_at) {
        const t = Date.parse(r.expires_at);
        if (Number.isFinite(t) && t > now && t < in30 && r.status !== 'revoked') expiring += 1;
      }
    }
    return { total, active, expiring, revoked };
  }, [rows]);

  const plans = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows ?? []) s.add(r.plan_tier);
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    return (rows ?? []).filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (planFilter !== 'all' && r.plan_tier !== planFilter) return false;
      return true;
    });
  }, [rows, statusFilter, planFilter]);

  const doMutation = async (action: 'revoke' | 'suspend' | 'resume', id: string): Promise<void> => {
    try {
      if (action === 'resume') {
        await api.resumeLicense(id);
      } else {
        const reason = prompt(`Reason for ${action}?`) ?? '';
        if (!reason.trim() && !confirm('Proceed without reason?')) return;
        if (action === 'revoke') await api.revokeLicense(id, reason);
        else await api.suspendLicense(id, reason);
      }
      onRefresh();
    } catch (e) {
      alert(`Could not ${action} the license. ${userMessageFor(e, 'this license')}`);
    }
  };

  const doDelete = async (id: string): Promise<void> => {
    if (!confirm('PERMANENTLY delete this license + cascade installations + anomalies? Audit log keeps a snapshot. Consider Revoke if you want to preserve history.')) return;
    try {
      await api.deleteLicense(id);
      setOpenDetailId(null);
      onRefresh();
    } catch (e) {
      alert(`Could not delete the license. ${userMessageFor(e, 'this license')}`);
    }
  };

  const columns: Column<LicenseRow>[] = [
    {
      key: 'license',
      header: 'License',
      sortable: true,
      sortValue: (r) => r.id,
      cell: (r) => (
        <PrimaryWithSub
          primary={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <KeyRound width={12} height={12} color="var(--text-muted)" />
              <code style={{ fontSize: '0.75rem' }}>{r.id.slice(0, 12)}…</code>
            </span>
          }
          sub={`customer ${r.customer_id.slice(0, 8)}…`}
        />
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      sortable: true,
      sortValue: (r) => PLAN_RANK[r.plan_tier] ?? 1000,
      cell: (r) => <PlanChip plan={r.plan_tier} />,
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      sortValue: (r) => r.license_type,
      cell: (r) => (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {r.license_type}
        </span>
      ),
    },
    {
      key: 'activations',
      header: 'Activations',
      align: 'right',
      sortable: true,
      sortValue: (r) => r.activation_count,
      cell: (r) => <ActivationsCell r={r} />,
    },
    {
      key: 'expires',
      header: 'Expires',
      align: 'right',
      sortable: true,
      // Never-expiring licenses have no date and sort last in both directions.
      sortValue: (r) => {
        if (!r.expires_at) return null;
        const t = Date.parse(r.expires_at);
        return Number.isFinite(t) ? t : null;
      },
      cell: (r) => <ExpiryCell iso={r.expires_at} />,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <StatusChip band={bandFromStatus(r.status)}>{r.status.replace(/_/g, ' ')}</StatusChip>,
    },
  ];

  const statusChips: Array<{ key: StatusFilter; label: string; count: number }> = [
    { key: 'all',       label: 'All',       count: rows?.length ?? 0 },
    { key: 'active',    label: 'Active',    count: (rows ?? []).filter((r) => r.status === 'active').length },
    { key: 'past_due',  label: 'Past due',  count: (rows ?? []).filter((r) => r.status === 'past_due').length },
    { key: 'suspended', label: 'Suspended', count: (rows ?? []).filter((r) => r.status === 'suspended').length },
    { key: 'revoked',   label: 'Revoked',   count: (rows ?? []).filter((r) => r.status === 'revoked').length },
    { key: 'expired',   label: 'Expired',   count: (rows ?? []).filter((r) => r.status === 'expired').length },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Licenses"
        subtitle={canMutate
          ? 'Issue, revoke, suspend, refund, or change plans across the entire customer base.'
          : 'View licenses across the entire customer base.'}
        icon={KeyRound}
        accent="#22d3ee"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw width={14} height={14}
                style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
              Refresh
            </Button>
            {canMutate && (
              <Button variant="default" size="sm" onClick={() => setIssueOpen(true)}>
                <Plus width={14} height={14} />
                Issue license
              </Button>
            )}
          </>
        }
      />

      <AccessNotice reason={canMutate ? null : `${perms.writeReason ?? ''} Issuing, revoking and suspending licenses is not available to you.`} />

      {rows && (
        <>
          <HeroPanel
            icon={KeyRound}
            accent="#22d3ee"
            title="Licensing posture"
            subtitle="Health and renewal load across active subscriptions."
            kpis={[
              <HeroKpi key="t" label="Total" value={counts.total} hint="all licenses" />,
              <HeroKpi key="a" label="Active" value={counts.active} band={counts.active > 0 ? 'healthy' : 'neutral'} />,
              <HeroKpi key="e" label="Expiring (30d)" value={counts.expiring}
                       band={counts.expiring > 0 ? 'degraded' : 'neutral'}
                       hint="needs renewal" />,
              <HeroKpi key="r" label="Revoked" value={counts.revoked}
                       band={counts.revoked > 0 ? 'critical' : 'neutral'} />,
            ]}
          />

          <div style={{ marginTop: 16 }}>
            <ExpiryTimeline rows={rows} />
          </div>
        </>
      )}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows && <Toolbar
          statusChips={statusChips}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          plans={plans}
          planFilter={planFilter}
          onPlanFilter={setPlanFilter}
        />}

        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={(r) => setOpenDetailId(r.id)}
          loading={!rows && !err}
          error={err}
          onRetry={onRefresh}
          emptyTitle={rows && rows.length === 0 ? 'No licenses yet' : 'No licenses match'}
          emptyDescription={rows && rows.length === 0
            ? (canMutate ? 'Issued licenses will appear here.' : 'No licenses have been issued yet.')
            : 'Adjust filters to see more rows.'}
        />
      </div>

      <DetailDrawer
        api={api}
        id={openDetailId}
        onClose={() => setOpenDetailId(null)}
        onSaved={onRefresh}
        canMutate={canMutate}
        canDelete={canDelete}
        writeReason={perms.writeReason}
        onAction={(a, id) => void doMutation(a, id)}
        onDelete={openDetailId ? () => void doDelete(openDetailId) : undefined}
      />

      <IssueDrawer
        api={api}
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        onIssued={() => { onRefresh(); }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Toolbar
 * ────────────────────────────────────────────────────────────────── */

function Toolbar({
  statusChips, statusFilter, onStatusFilter,
  plans, planFilter, onPlanFilter,
}: {
  statusChips: Array<{ key: StatusFilter; label: string; count: number }>;
  statusFilter: StatusFilter;
  onStatusFilter: (s: StatusFilter) => void;
  plans: string[];
  planFilter: string;
  onPlanFilter: (p: string) => void;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex', gap: 12, flexWrap: 'wrap',
        alignItems: 'center', justifyContent: 'space-between',
      }}
    >
      <ChipRow chips={statusChips} value={statusFilter} onChange={onStatusFilter} />
      <select
        aria-label="Filter by plan"
        value={planFilter}
        onChange={(e) => onPlanFilter(e.target.value)}
        style={{
          background: 'var(--bg-overlay)',
          border: '1px solid var(--border)',
          color: 'var(--text-primary)',
          padding: '7px 10px',
          borderRadius: 8,
          fontSize: '0.8125rem',
        }}
      >
        <option value="all">All plans</option>
        {plans.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
    </div>
  );
}

function ChipRow<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string; count: number }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filter by status"
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        borderRadius: 12,
        border: '1px solid var(--border)',
        background: 'var(--bg-overlay)',
        flexWrap: 'wrap',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              display: 'inline-flex', gap: 5, alignItems: 'center',
              padding: '5px 9px', border: 'none', borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {c.label}
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.6875rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                padding: '0 6px',
                borderRadius: 999,
                minWidth: 18, textAlign: 'center',
              }}
            >{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Plan chip + activations + expiry cells
 * ────────────────────────────────────────────────────────────────── */

function PlanChip({ plan }: { plan: string }): JSX.Element {
  const tone = PLAN_TONES[plan] ?? 'var(--text-muted)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: '0.6875rem',
        fontWeight: 500,
        background: `${tone}1f`,
        color: tone,
        border: `1px solid ${tone}40`,
        textTransform: 'capitalize',
      }}
    >
      {plan}
    </span>
  );
}

function ActivationsCell({ r }: { r: LicenseRow }): JSX.Element {
  const pct = r.max_activations === 0 ? 0 : (r.activation_count / r.max_activations);
  let tone = 'var(--success)';
  if (pct >= 1) tone = 'var(--danger)';
  else if (pct >= 0.8) tone = 'var(--warning)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <span style={{ color: tone, fontWeight: 500 }}>{r.activation_count}</span>
      <span style={{ color: 'var(--text-muted)' }}>/ {r.max_activations}</span>
    </span>
  );
}

function ExpiryCell({ iso }: { iso: string | null }): JSX.Element {
  if (!iso) return <span style={{ color: 'var(--text-muted)' }}>never</span>;
  const t = Date.parse(iso);
  const now = Date.now();
  const diffDays = Math.round((t - now) / (24 * 3600 * 1000));
  let tone = 'var(--text-muted)';
  if (diffDays < 0) tone = 'var(--danger)';
  else if (diffDays <= 30) tone = 'var(--warning)';
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <span style={{ fontSize: '0.75rem', color: tone, fontWeight: 500 }}>
        {iso.slice(0, 10)}
      </span>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
        {diffDays < 0 ? `expired ${-diffDays}d ago` : `in ${diffDays}d`}
      </span>
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * 6-month expiry timeline strip
 * ────────────────────────────────────────────────────────────────── */

function ExpiryTimeline({ rows }: { rows: LicenseRow[] }): JSX.Element {
  const buckets = useMemo(() => {
    const months: Array<{ label: string; key: string; count: number; revenue: number }> = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push({
        label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        key: `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`,
        count: 0,
        revenue: 0,
      });
    }
    for (const r of rows) {
      if (!r.expires_at || r.status === 'revoked') continue;
      const t = Date.parse(r.expires_at);
      if (!Number.isFinite(t)) continue;
      const dt = new Date(t);
      const k = `${dt.getFullYear()}-${(dt.getMonth() + 1).toString().padStart(2, '0')}`;
      const b = months.find((m) => m.key === k);
      if (b) b.count += 1;
    }
    return months;
  }, [rows]);

  const max = Math.max(1, ...buckets.map((b) => b.count));

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <CalendarClock width={14} height={14} color="var(--text-muted)" />
        <span style={{
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}>
          Renewal load · next 6 months
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`,
          gap: 12,
          alignItems: 'end',
        }}
      >
        {buckets.map((b) => {
          const h = Math.max(4, (b.count / max) * 80);
          const tone = b.count === 0 ? '#475569' : b.count > max * 0.7 ? '#fb923c' : '#22d3ee';
          return (
            <div key={b.key} style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
              <div
                style={{
                  width: '100%',
                  height: 80,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: '70%',
                    height: h,
                    borderRadius: 6,
                    background: `linear-gradient(180deg, ${tone}, ${tone}55)`,
                    border: `1px solid ${tone}99`,
                  }}
                  title={`${b.count} expir${b.count === 1 ? 'y' : 'ies'}`}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: tone, fontVariantNumeric: 'tabular-nums' }}>
                  {b.count}
                </span>
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{b.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Detail drawer — full LicenseDetail + audit trail + billing actions
 * ────────────────────────────────────────────────────────────────── */

function DetailDrawer({
  api, id, onClose, onSaved, canMutate, canDelete, writeReason, onAction, onDelete,
}: {
  api: AdminApiClient;
  id: string | null;
  onClose: () => void;
  onSaved: () => void;
  canMutate: boolean;
  canDelete: boolean;
  writeReason: string | null;
  onAction: (action: 'revoke' | 'suspend' | 'resume', id: string) => void;
  onDelete?: () => void;
}): JSX.Element {
  const [detail, setDetail] = useState<LicenseDetail | null>(null);
  const [audit, setAudit] = useState<AuditLogRow[] | null>(null);
  const [auditErr, setAuditErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const lastIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) { setDetail(null); setAudit(null); return; }
    if (lastIdRef.current === id) return;
    lastIdRef.current = id;
    setDetail(null); setAudit(null); setAuditErr(null); setErr(null); setMsg(null);
    void api.licenseDetail(id).then(setDetail).catch((e) => setErr(failureFrom(e, 'this license').message));
    void api.auditLogs({ limit: 200 })
      .then((r) => setAudit(r.audit_logs.filter((a) => a.target_id === id || a.target_type === 'license')))
      .catch((e) => setAuditErr(failureFrom(e, 'the audit trail').message));
  }, [id, api]);

  const errMsg = (e: unknown): string => userMessageFor(e, 'this license');

  const run = async (label: string, fn: () => Promise<string>): Promise<void> => {
    setBusy(label); setMsg(null); setErr(null);
    try { setMsg(await fn()); onSaved(); }
    catch (e) { setErr(errMsg(e)); }
    finally { setBusy(null); }
  };

  if (!id) return <Drawer open={false} onClose={onClose} title="">{<></>}</Drawer>;

  return (
    <Drawer
      open
      onClose={() => { lastIdRef.current = null; onClose(); }}
      width="lg"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <KeyRound width={14} height={14} />
          <code style={{ fontSize: '0.875rem' }}>{id.slice(0, 12)}…</code>
        </span>
      }
      subtitle={detail ? `${detail.company_name} · ${detail.plan_tier} ${detail.license_type}` : undefined}
      footer={
        detail && (
          <>
            {/* V2 / locked-model — full detail with capacity + lifecycle tabs. Read access for every role. */}
            <Link
              to={`/licenses/${detail.id}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 4,
                border: '1px solid var(--border)',
                fontSize: '0.8125rem', textDecoration: 'none',
                color: 'var(--text)',
              }}
            >
              View full detail →
            </Link>
            {!canMutate && writeReason && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                {writeReason}
              </span>
            )}
            {canMutate && canDelete && onDelete && (
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <Trash2 width={14} height={14} />
                Delete
              </Button>
            )}
            {canMutate && detail.status !== 'revoked' && detail.status !== 'suspended' && (
              <Button variant="outline" size="sm" onClick={() => onAction('suspend', detail.id)}>
                <Pause width={14} height={14} />
                Suspend
              </Button>
            )}
            {canMutate && detail.status === 'suspended' && (
              <Button variant="outline" size="sm" onClick={() => onAction('resume', detail.id)}>
                <Play width={14} height={14} />
                Resume
              </Button>
            )}
            {canMutate && detail.status !== 'revoked' && (
              <Button variant="destructive" size="sm" onClick={() => onAction('revoke', detail.id)}>
                <ShieldOff width={14} height={14} />
                Revoke
              </Button>
            )}
          </>
        )
      }
    >
      {err && <p className="err" role="alert" style={{ fontSize: '0.8125rem' }}>{err}</p>}
      {msg && <p role="status" style={{ color: 'var(--accent-text)', fontSize: '0.8125rem' }}>{msg}</p>}
      {!detail && !err && <p role="status" style={{ color: 'var(--text-muted)' }}>Loading detail…</p>}

      {detail && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Section title="Customer">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <KV label="Company">{detail.company_name}</KV>
              <KV label="Email">{detail.primary_email}</KV>
              <KV label="Status"><StatusChip band={bandFromStatus(detail.status)}>{detail.status.replace(/_/g, ' ')}</StatusChip></KV>
              <KV label="Residency">{detail.residency_region}</KV>
            </div>
          </Section>

          <Section title="License">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <KV label="Plan"><PlanChip plan={detail.plan_tier} /></KV>
              <KV label="Type">{detail.license_type}</KV>
              <KV label="Activations">
                <ActivationsCell r={{
                  ...detail,
                  // LicenseDetail has these but TS sees LicenseRow shape
                } as unknown as LicenseRow} />
              </KV>
              <KV label="Issued">{detail.issued_at.slice(0, 10)}</KV>
              <KV label="Expires">
                <ExpiryCell iso={detail.expires_at} />
              </KV>
              <KV label="Activation key">
                <SecretValue value={detail.activation_key} redacted={detail.activation_key_redacted} />
              </KV>
            </div>
          </Section>

          <Section title="Billing">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <KV label="Stripe customer">
                <code style={{ fontSize: '0.75rem' }}>{detail.stripe_customer_id ?? '—'}</code>
              </KV>
              <KV label="Subscription">
                <code style={{ fontSize: '0.75rem' }}>{detail.stripe_subscription_id ?? '—'}</code>
              </KV>
              <KV label="Last invoice">
                {detail.last_invoice_amount !== null && detail.last_invoice_at
                  ? <>${(detail.last_invoice_amount / 100).toFixed(2)} · {detail.last_invoice_at.slice(0, 10)}</>
                  : <span style={{ color: 'var(--text-muted)' }}>none yet</span>}
              </KV>
              <KV label="Override">
                {detail.override_source
                  ? `${detail.override_source} (base $${((detail.override_base ?? 0) / 100).toFixed(2)})`
                  : <span style={{ color: 'var(--text-muted)' }}>plan default</span>}
              </KV>
            </div>

            {canMutate && (
              <div
                style={{
                  marginTop: 12, paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                  display: 'flex', flexWrap: 'wrap', gap: 8,
                }}
              >
                <Button
                  variant="outline" size="sm"
                  disabled={!detail.stripe_customer_id || busy !== null}
                  onClick={() => void run('checkout', async () => {
                    const tier = prompt('Plan tier (starter/business/enterprise):', detail.plan_tier);
                    if (tier && !['starter', 'business', 'enterprise'].includes(tier)) {
                      throw new Error('invalid plan tier');
                    }
                    const res = await api.sendCheckoutLink(detail.customer_id, {
                      license_id: detail.id,
                      plan_tier: (tier as 'starter' | 'business' | 'enterprise' | null) ?? undefined,
                    });
                    return `Checkout link ${res.email_sent ? 'emailed' : 'created'}: ${res.checkout_url}`;
                  })}
                >
                  <Send width={14} height={14} />
                  {busy === 'checkout' ? 'Sending…' : 'Send checkout link'}
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={!detail.stripe_subscription_id || busy !== null}
                  onClick={() => void run('cancel', async () => {
                    const atPeriodEnd = confirm(
                      'Cancel at period end? OK = at period end (customer keeps access until renewal). ' +
                      'Cancel = cancel immediately.',
                    );
                    const reason = prompt('Reason (required for audit):') ?? '';
                    if (!reason.trim()) throw new Error('reason is required');
                    const res = await api.cancelSubscription(detail.id, { at_period_end: atPeriodEnd, reason });
                    return `Cancelled · ${res.status} (cancel_at_period_end=${res.cancel_at_period_end})`;
                  })}
                >
                  {busy === 'cancel' ? 'Cancelling…' : 'Cancel subscription'}
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={!detail.stripe_subscription_id || busy !== null}
                  onClick={() => void run('refund', async () => {
                    const amtStr = prompt('Refund amount in cents (blank = full refund):') ?? '';
                    const reason = prompt('Reason (required for audit):') ?? '';
                    if (!reason.trim()) throw new Error('reason is required');
                    const body: { amount_cents?: number; reason: string } = { reason };
                    if (amtStr.trim()) {
                      const n = Number(amtStr);
                      if (!Number.isFinite(n) || n <= 0) throw new Error('invalid amount');
                      body.amount_cents = Math.round(n);
                    }
                    const res = await api.refundLatestPayment(detail.id, body);
                    return `Refunded $${(res.amount_cents / 100).toFixed(2)} (${res.status})`;
                  })}
                >
                  <CreditCard width={14} height={14} />
                  {busy === 'refund' ? 'Refunding…' : 'Refund'}
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={!detail.stripe_subscription_id || busy !== null}
                  onClick={() => void run('changeplan', async () => {
                    const tier = prompt('New plan tier (starter/business/enterprise):', detail.plan_tier);
                    if (!tier) throw new Error('cancelled');
                    if (!['starter', 'business', 'enterprise'].includes(tier)) throw new Error('invalid plan tier');
                    const res = await api.changePlan(detail.id, {
                      new_plan_tier: tier as 'starter' | 'business' | 'enterprise',
                    });
                    return `Plan changed to ${res.plan_tier}`;
                  })}
                >
                  <RotateCcw width={14} height={14} />
                  {busy === 'changeplan' ? 'Changing…' : 'Change plan'}
                </Button>
              </div>
            )}
          </Section>

          <Section title="Features">
            {(detail.features_json ?? []).length === 0 ? (
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>—</span>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {detail.features_json.map((f) => (
                  <span
                    key={f}
                    style={{
                      fontSize: '0.6875rem',
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: 'var(--bg-overlay)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    {f}
                  </span>
                ))}
              </div>
            )}
          </Section>

          <Section title="Audit trail" icon={ScrollText}>
            {audit === null && !auditErr && <p role="status" style={{ color: 'var(--text-muted)' }}>Loading…</p>}
            {auditErr && <p role="alert" style={{ color: 'var(--danger)', fontSize: '0.8125rem', margin: 0 }}>{auditErr}</p>}
            {audit && audit.length === 0 && (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }}>
                No audit entries scoped to this license yet.
              </p>
            )}
            {audit && audit.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {audit.slice(0, 12).map((a) => (
                  <li
                    key={a.id}
                    style={{
                      fontSize: '0.75rem',
                      padding: '6px 10px',
                      borderRadius: 6,
                      background: 'var(--bg-overlay)',
                      border: '1px solid var(--border)',
                      display: 'flex', flexDirection: 'column', gap: 2,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <code style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{a.action}</code>
                      <span style={{ color: 'var(--text-muted)' }}>{a.created_at.slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {a.actor_type}{a.actor_id ? ` · ${a.actor_id.slice(0, 8)}…` : ''}
                      {a.reason && ` · ${a.reason}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Issue license drawer
 * ────────────────────────────────────────────────────────────────── */

// License features the Vloud engine actually gates on — the authoritative
// set. Source of truth: the engine repo's `license/state.ts` defaultClaims()
// plus every `requireFeature('…')` call site in `app.ts`. `core` is the
// always-on base; the rest are tier-gated. The license server does NOT
// validate features (the issue endpoint accepts any string[]), so this list —
// not a server whitelist — is what an operator should actually pick from.
const LICENSE_FEATURES = [
  'core',
  'security-engine',
  'ai-doctor',
  'multi-region',
  'kubernetes',
  'reseller',
  'white-label',
] as const;

function IssueDrawer({
  api, open, onClose, onIssued,
}: {
  api: AdminApiClient;
  open: boolean;
  onClose: () => void;
  onIssued: () => void;
}): JSX.Element {
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [customerId, setCustomerId] = useState<string>('');
  const [planTier, setPlanTier] = useState<'starter' | 'business' | 'enterprise'>('starter');
  const [licenseType, setLicenseType] = useState<'monthly' | 'yearly' | 'lifetime' | 'offline'>('monthly');
  const [maxActivations, setMaxActivations] = useState(3);
  // Offline licences (ADR-014, C03-T05): signed for their whole validity so
  // the bound engine runs without reaching the licence server — the escape
  // from the loop where the host that runs this service needs this service
  // to stay licensed. They are fixed until re-issued, hence explicit limits.
  const [validityDays, setValidityDays] = useState(365);
  const [maxInstances, setMaxInstances] = useState('');
  const [maxLinuxUsers, setMaxLinuxUsers] = useState('');
  const offline = licenseType === 'offline';
  // Multi-select via a Set (same pattern as the engine's Feature Manager /
  // AdminPackagesPage), replacing the old comma-separated free-text field.
  const [features, setFeatures] = useState<Set<string>>(new Set());
  // Optional: bind the new license straight to a registered engine's
  // machine_id. Without this a customer license is bound to nobody
  // (machine_id_binding = NULL) so no engine ever picks it up — the
  // operator would have to hand the activation key to the customer to
  // paste on the engine. Binding here activates the chosen engine on
  // its next pull (≤60s), which is what "issue a license and the engine
  // lights up" needs.
  const [engines, setEngines] = useState<EngineRow[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [enginesErr, setEnginesErr] = useState(false);
  const [retry, setRetry] = useState(0);
  const [machineId, setMachineId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Success payload — shown in place of the form so the operator sees the
  // activation key + the exact next step (was: drawer just closed, key
  // never surfaced, engine stayed dark).
  const [issued, setIssued] = useState<{ key: string; boundEngine: EngineRow | null; bindError?: string } | null>(null);

  const toggleFeature = (f: string): void => {
    setFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f); else next.add(f);
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    // Reset any prior success view when the drawer is re-opened.
    setIssued(null);
    setErr(null);
    setLoadErr(null);
    setEnginesErr(false);
    api.customers(500)
      .then((r) => setCustomers(r.customers))
      .catch((e) => { setCustomers([]); setLoadErr(failureFrom(e, 'the customer list').message); });
    api.engines()
      .then((r) => setEngines(r.engines))
      .catch((e) => { setEngines([]); setEnginesErr(true); failureFrom(e, 'the engine list'); });
  }, [api, open, retry]);

  // Only engines with no active bound license can be bound here.
  const bindableEngines = engines.filter((e) => !e.bound_license_id);

  const submit = async (): Promise<void> => {
    if (!customerId) { setErr('Pick a customer'); return; }
    // An offline licence reaches its engine only through the machine
    // binding (the pull signs it offline); unbound, it would sit unused.
    if (offline && !machineId) { setErr('An offline licence must be bound to an engine'); return; }
    if (!offline && (!Number.isInteger(maxActivations) || maxActivations < 1 || maxActivations > 10000)) {
      setErr('Max activations must be a whole number between 1 and 10000');
      return;
    }
    const limits: { max_instances?: number; max_linux_users?: number } = {};
    if (maxInstances.trim()) limits.max_instances = Number(maxInstances);
    if (maxLinuxUsers.trim()) limits.max_linux_users = Number(maxLinuxUsers);
    if (Object.values(limits).some((n) => !Number.isInteger(n) || n < 1)) {
      setErr('Limits must be whole numbers of at least 1');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const featureList = [...features];
      const res = await api.issueLicense({
        customer_id: customerId,
        plan_tier: planTier,
        license_type: licenseType,
        max_activations: offline ? 1 : maxActivations,
        features: featureList.length ? featureList : undefined,
        validity_days: offline ? validityDays : undefined,
        limits: Object.keys(limits).length ? limits : undefined,
      });
      let boundEngine: EngineRow | null = null;
      let bindError: string | undefined;
      if (machineId) {
        // Reuse the existing bind-machine primitive so the chosen engine
        // gets this license on its next heartbeat/pull. The license already
        // exists at this point: a bind failure must NOT look like an issue
        // failure (the operator would retry and issue a duplicate).
        try {
          await api.bindLicenseToMachine(res.id, machineId);
          boundEngine = engines.find((e) => e.machine_id === machineId) ?? null;
        } catch (bindE) {
          bindError = userMessageFor(bindE, 'the engine');
        }
      }
      setIssued({ key: res.activation_key, boundEngine, bindError });
      setCustomerId('');
      setFeatures(new Set());
      setMaxActivations(3);
      setMaxInstances('');
      setMaxLinuxUsers('');
      setMachineId('');
      onIssued();
    } catch (e) {
      setErr(userMessageFor(e, 'the license'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="md"
      title="Issue new license"
      subtitle="Pick a customer, plan, and key shape — the activation key generates server-side."
      footer={
        issued ? (
          <Button variant="default" size="sm" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => void submit()}
              disabled={busy || !customerId || (offline && !machineId)}
            >
              {busy ? 'Issuing…' : offline ? 'Issue offline license' : 'Issue license'}
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <IssuedResult issued={issued} onIssueAnother={() => setIssued(null)} />
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <p className="err" role="alert" style={{ fontSize: '0.8125rem' }}>{err}</p>}
        {loadErr && (
          <p role="alert" className="err" style={{ fontSize: '0.8125rem' }}>
            {loadErr}{' '}
            <button type="button" onClick={() => setRetry((n) => n + 1)}>Retry</button>
          </p>
        )}

        <Field label="Customer">
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            style={inputStyle}
          >
            <option value="">— pick customer —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name} ({c.primary_email})
              </option>
            ))}
          </select>
        </Field>

        <Field label="Plan tier">
          <SegmentedRadio
            value={planTier}
            onChange={(v) => setPlanTier(v as typeof planTier)}
            options={[
              { value: 'starter',    label: 'Starter' },
              { value: 'business',   label: 'Business' },
              { value: 'enterprise', label: 'Enterprise' },
            ]}
          />
        </Field>

        <Field label="License type">
          <SegmentedRadio
            value={licenseType}
            onChange={(v) => setLicenseType(v as typeof licenseType)}
            options={[
              { value: 'monthly',  label: 'Monthly' },
              { value: 'yearly',   label: 'Yearly' },
              { value: 'lifetime', label: 'Lifetime' },
              { value: 'offline',  label: 'Offline' },
            ]}
          />
        </Field>

        {offline ? (
          <Field
            label="Validity (days)"
            hint="Offline: signed for the whole period and never needs the licence server — the engine keeps running through any outage, and stops hard at the end. Max 365 days; binding an engine is required."
          >
            <input
              type="number"
              min={1}
              max={365}
              value={validityDays}
              onChange={(e) => setValidityDays(Math.min(365, Math.max(1, Number(e.target.value) || 1)))}
              style={inputStyle}
            />
          </Field>
        ) : (
          <Field label="Max activations" hint="How many machines can run this license at once.">
            <input
              type="number"
              min={1}
              max={10000}
              value={maxActivations}
              onChange={(e) => setMaxActivations(Number(e.target.value))}
              style={inputStyle}
            />
          </Field>
        )}

        <Field
          label="Limits (optional)"
          hint={offline
            ? 'Fixed for the life of an offline licence. Empty = 10 instances and 5 hosting accounts (what a bound engine is signed with), not the plan tier’s defaults — set them.'
            : 'Empty: a bound engine is signed with 10 instances and 5 hosting accounts; activation with the key uses the plan tier’s defaults.'}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              min={1}
              placeholder="Max instances"
              aria-label="Max instances"
              value={maxInstances}
              onChange={(e) => setMaxInstances(e.target.value)}
              style={inputStyle}
            />
            <input
              type="number"
              min={1}
              placeholder="Max hosting accounts"
              aria-label="Max hosting accounts"
              value={maxLinuxUsers}
              onChange={(e) => setMaxLinuxUsers(e.target.value)}
              style={inputStyle}
            />
          </div>
        </Field>

        <Field
          label={offline ? 'Bind to engine (required)' : 'Bind to engine (optional)'}
          hint={
            enginesErr
              ? 'The engine list could not be loaded, so binding is unavailable right now. You can issue without binding and hand the key to the customer, or reopen this form to retry.'
              : bindableEngines.length
              ? (offline
                  ? 'The engine that will run on this offline licence. It picks it up on its next pull (≤60s).'
                  : 'Pick a registered engine to activate it directly — it goes licensed on its next heartbeat (≤60s). Leave unbound to hand the activation key to the customer instead.')
              : 'No unbound engines are registered. The customer activates by pasting the key on their engine’s Settings → License.'
          }
        >
          <select
            value={machineId}
            onChange={(e) => setMachineId(e.target.value)}
            style={inputStyle}
            disabled={!bindableEngines.length}
            aria-label={offline ? 'Bind to engine (required)' : 'Bind to engine (optional)'}
          >
            <option value="">— don’t bind (activate later with the key) —</option>
            {bindableEngines.map((e) => (
              <option key={e.machine_id} value={e.machine_id}>
                {(e.hostname ?? e.node_name ?? e.machine_id.slice(0, 12))}
                {e.engine_version ? ` · ${e.engine_version}` : ''}
                {` · ${e.machine_id.slice(0, 8)}…`}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Features" hint="Tick the entitlements to grant. None ticked = plan defaults.">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {LICENSE_FEATURES.map((f) => {
              const on = features.has(f);
              return (
                <label
                  key={f}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
                    fontSize: '0.8125rem', userSelect: 'none',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    background: on ? 'var(--accent-soft, rgba(99,102,241,0.12))' : 'var(--bg-overlay)',
                    color: on ? 'var(--accent)' : 'var(--text-primary)',
                  }}
                >
                  <input type="checkbox" checked={on} onChange={() => toggleFeature(f)} style={{ margin: 0 }} />
                  {f}
                </label>
              );
            })}
          </div>
        </Field>
      </div>
      )}
    </Drawer>
  );
}

/**
 * Post-issue confirmation. Surfaces the activation key (previously the
 * drawer just closed and the key was never shown) and spells out the
 * one remaining step so the operator knows whether the engine will
 * light up on its own or needs the key pasted.
 */
function IssuedResult({
  issued, onIssueAnother,
}: {
  issued: { key: string; boundEngine: EngineRow | null; bindError?: string };
  onIssueAnother: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard?.writeText(issued.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const engineLabel = issued.boundEngine
    ? (issued.boundEngine.hostname ?? issued.boundEngine.node_name
        ?? issued.boundEngine.machine_id.slice(0, 12))
    : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          padding: '10px 12px', borderRadius: 10,
          border: '1px solid var(--accent)',
          background: 'var(--accent-soft, rgba(99,102,241,0.12))',
          fontSize: '0.8125rem', color: 'var(--text-primary)',
        }}
      >
        License issued.
      </div>
      {issued.bindError && (
        <div
          role="alert"
          style={{
            padding: '10px 12px', borderRadius: 10, fontSize: '0.8125rem',
            border: '1px solid var(--warning)', background: 'var(--warning-soft)',
            color: 'var(--text-primary)',
          }}
        >
          The license was issued, but it could not be bound to the selected engine: {issued.bindError}
          {' '}Do not issue it again. Bind it from the license’s detail view, or give the customer the key below.
        </div>
      )}

      <Field label="Activation key">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <code
            data-testid="issued-key"
            style={{
              flex: 1, padding: 8, borderRadius: 8, fontSize: '0.8125rem',
              border: '1px solid var(--border)', background: 'var(--bg-overlay)',
              wordBreak: 'break-all',
            }}
          >
            {issued.key}
          </code>
          <Button variant="ghost" size="sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
        </div>
      </Field>

      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {issued.bindError ? null : engineLabel ? (
          <>
            Bound to <strong style={{ color: 'var(--text-primary)' }}>{engineLabel}</strong>.
            That engine goes licensed on its next heartbeat (≤&nbsp;60s) — no key entry needed.
            Watch the Engines page or the engine’s Settings&nbsp;→&nbsp;License to confirm.
          </>
        ) : (
          <>
            Not bound to an engine. Send this key to the customer and have them paste it under
            Settings&nbsp;→&nbsp;License on their engine (or bind it to a registered engine from the
            license’s detail view). The engine stays unlicensed until then.
          </>
        )}
      </div>

      <div>
        <Button variant="ghost" size="sm" onClick={onIssueAnother}>Issue another</Button>
      </div>
    </div>
  );
}

function SegmentedRadio({
  value, onChange, options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
      }}
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            style={{
              padding: '6px 12px', border: 'none', borderRadius: 7,
              // Selected pill must be unmistakable in dark mode — the old
              // `--bg-raised` on a `--bg-overlay` track was near-invisible.
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? '#fff' : 'var(--text-muted)',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.25)' : 'none',
              fontSize: '0.75rem', fontWeight: active ? 600 : 500,
              cursor: 'pointer', transition: 'background 0.12s, color 0.12s',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers (Section, KV, Field)
 * ────────────────────────────────────────────────────────────────── */

function Section({
  title, icon: Icon, children,
}: { title: string; icon?: typeof ScrollText; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {Icon && <Icon width={11} height={11} />}
        {title}
      </h3>
      {children}
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{label}</span>
      <div style={{ fontSize: '0.8125rem' }}>{children}</div>
    </div>
  );
}

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  const uid = useId();
  const hintId = `${uid}-hint`;
  // A single native control gets a real <label for>; a composite control
  // (segmented buttons, checkbox set) is exposed as a labelled group.
  const single = isValidElement(children)
    && typeof (children as ReactElement).type === 'string'
    && ['input', 'select', 'textarea'].includes((children as ReactElement).type as string);
  const control = single
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: uid,
        'aria-describedby': hint ? hintId : undefined,
      })
    : children;
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
      {...(single ? {} : { role: 'group', 'aria-labelledby': `${uid}-label` })}
    >
      {single
        ? <label htmlFor={uid} style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</label>
        : <span id={`${uid}-label`} style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>}
      {control}
      {hint && <span id={hintId} style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: 8,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-overlay)',
  color: 'var(--text-primary)',
  fontSize: '0.8125rem',
  outline: 'none',
} as const;
